import {
  AnnotateSVG,
  Button,
  Checkbox,
  GraphicOrganizerSVG,
  GuidedHighlightingSVG,
  Icon,
  InsightsSVG,
  PowerWordFilledSVG,
  QuizSVG,
  WriteSVG,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { useState } from "react";

import {
  ASSESSMENT_TYPE,
  AttachmentWrapper,
} from "@/types/models/ContentAttachment.interface";
import { EventProperties } from "@/types/models/EventProperties.interface";
import capitalize from "@/utils/capitalize";
import GuidedHighlightingTeacherInfoPopup from "@/view/GuidedHighlightingContainer/GuidedHighlightingTeacherInfoPopup";
import useAssignmentActivitySelectionUpdate from "@/view/utils/useAssignmentActivitySelectionUpdate";
import {
  activityIconCss,
  betaTagCss,
  checkboxCss,
  containerCss,
  filledIconCss,
  hollowIconCss,
  labelCss,
  viewButtonCss,
  viewableContainerCss,
} from "./ViewableActivity.style";

interface ViewableActivityProps {
  assessmentType?: ASSESSMENT_TYPE;
  attachment?: AttachmentWrapper;
  buttonDataProps: {
    "data-qa-selector": string;
  };
  checkboxDataProps: {
    "data-qa-selector": string;
  };
  dispatch: () => void;
  eventProperties: EventProperties;
  isElaSkillsQuiz?: boolean;
  isSelected: boolean;
  isViewableOnly?: boolean;
  label: string;
  onClick: (a?: AttachmentWrapper) => void;
  setIsPollSelected?: () => void;
  setVisibleActivityIds: () => void;
  showBetaTag?: boolean;
  showViewButton?: boolean;
  toggleChecksForUnderstanding?: () => void;
  toggleGuidedHighlighting?: () => void;
  toggleGraphicOrganizers?: (isChecked: boolean) => void;
  visibleActivityIds: string[];
}

const ButtonWithTrackEvent = withTrackEvent(
  Button,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Button> & React.RefAttributes<HTMLButtonElement>
>;
const CheckboxWithTrackEvent = withTrackEvent(
  Checkbox,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Checkbox> & React.RefAttributes<HTMLInputElement>
>;

const ViewableActivity = ({
  assessmentType,
  attachment,
  buttonDataProps,
  checkboxDataProps,
  dispatch,
  eventProperties,
  isElaSkillsQuiz = false,
  isViewableOnly = false,
  isSelected,
  label,
  onClick,
  setIsPollSelected,
  setVisibleActivityIds,
  visibleActivityIds,
  showBetaTag,
  showViewButton = true,
  toggleChecksForUnderstanding,
  toggleGuidedHighlighting,
  toggleGraphicOrganizers,
}: ViewableActivityProps) => {
  const { isChecked, onClick: onClickCheckbox } =
    useAssignmentActivitySelectionUpdate({
      attachmentId: attachment?.id,
      dispatch,
      isElaSkillsQuiz,
      isSelected,
      setIsPollSelected,
      setVisibleActivityIds,
      toggleChecksForUnderstanding,
      toggleGuidedHighlighting,
      toggleGraphicOrganizers,
      visibleActivityIds,
    });

  const [isGuidedHighlightingPopupActive, setIsGuidedHighlightingPopupActive] =
    useState(false);

  const activityType = assessmentType ?? attachment?.content_type;

  if (!activityType) {
    return null;
  }

  const onClickViewButton = () => {
    if (activityType === "GUIDED_HIGHLIGHTING_QUIZ") {
      setIsGuidedHighlightingPopupActive(true);
    } else if (attachment) {
      onClick(attachment);
    } else {
      onClick();
    }
  };

  const onClickGetStarted = () => {
    if (attachment) {
      onClick(attachment);
    } else {
      onClick();
    }
  };

  const formatObjectType = (attachment: AttachmentWrapper | undefined) => {
    if (attachment) {
      const contentType = attachment?.content_type;
      const splitWord = contentType?.split("_");
      const allLowerCase = splitWord?.map((word) => word.toLowerCase());
      const correctlyCapitalizedWords = allLowerCase?.map((word, index) => {
        return index === 0 ? word : capitalize(word);
      });

      const joinedWord = correctlyCapitalizedWords?.join("");
      const keyWord =
        joinedWord === "powerWordsActivity" ? "powerWords" : joinedWord;

      return objectTypes[keyWord as keyof typeof objectTypes];
    } else {
      let objectType;
      switch (activityType) {
        case "CHECKS_FOR_UNDERSTANDING_QUIZ":
          objectType = "Checks For Understanding Quiz";
          break;
        case "GUIDED_HIGHLIGHTING_QUIZ":
          objectType = "Guided Highlighting";
          break;
        case "POLL":
          objectType = "Poll";
          break;
      }
      return objectType;
    }
  };

  const activityIcon = (() => {
    let icon;

    switch (activityType) {
      case "ANNOTATIONS":
        icon = AnnotateSVG;
        break;
      case "CHECKS_FOR_UNDERSTANDING_QUIZ":
      case "QUIZ":
        icon = QuizSVG;
        break;
      case "GUIDED_HIGHLIGHTING_QUIZ":
        icon = GuidedHighlightingSVG;
        break;
      case "POLL":
        icon = InsightsSVG;
        break;
      case "POWER_WORDS_ACTIVITY":
        icon = PowerWordFilledSVG;
        break;
      case "WRITE_PROMPT":
        icon = WriteSVG;
        break;
      case "GRAPHIC_ORGANIZERS":
        icon = GraphicOrganizerSVG;
        break;
    }

    return icon as
      | React.ComponentPropsWithoutRef<typeof Icon>["SvgComponent"]
      | undefined;
  })();

  const renderViewButton = () => {
    if (!showViewButton) return null;

    return (
      <ButtonWithTrackEvent
        __cssFor={{ root: viewButtonCss }}
        dataProps={buttonDataProps}
        flavor={Button.flavors.tertiary}
        onClick={() => onClickViewButton()}
        {...eventProperties}
      >
        View
      </ButtonWithTrackEvent>
    );
  };

  return (
    <div css={containerCss}>
      {isViewableOnly ? (
        <div css={viewableContainerCss}>
          <div css={labelCss(showViewButton)}>
            {activityIcon && (
              <Icon
                __cssFor={{
                  root: activityIconCss(activityType),
                }}
                size={Icon.sizes.md}
                SvgComponent={activityIcon}
                isDecorative
              />
            )}
            Annotations
            {renderViewButton()}
          </div>
        </div>
      ) : (
        <CheckboxWithTrackEvent
          __cssFor={{
            checkedBox: filledIconCss,
            emptyCheckbox: hollowIconCss,
            labelText: labelCss(showViewButton),
            root: checkboxCss(isChecked),
          }}
          checked={isChecked}
          dataProps={checkboxDataProps}
          /* @ts-expect-error Label prop type does not need to be fixed as a string */
          label={
            <>
              {activityIcon && (
                <Icon
                  __cssFor={{
                    root: activityIconCss(activityType),
                  }}
                  size={Icon.sizes.md}
                  SvgComponent={activityIcon}
                  isDecorative
                />
              )}
              {label}
              {renderViewButton()}
            </>
          }
          name={label}
          onClick={onClickCheckbox}
          actionName={actionNames.update}
          actionPrefix={actionPrefixes.initiate}
          // TODO: remove the checks for understanding string after FEP team adds 'ChecksForUnderstandingQuiz' to allowable objectType options
          objectType={formatObjectType(attachment)}
          streamProperties={{
            description: label,
          }}
        />
      )}
      {showBetaTag && (
        <>
          <span css={betaTagCss}>Beta</span>
        </>
      )}
      {showViewButton && (
        <GuidedHighlightingTeacherInfoPopup
          onClickGetStarted={onClickGetStarted}
          active={isGuidedHighlightingPopupActive}
          setActive={setIsGuidedHighlightingPopupActive}
        />
      )}
    </div>
  );
};

export default ViewableActivity;
